import { PRODUCT_TYPE } from '@/utils/enum';

/**
 * Helpers cho combo có sản phẩm con IMEI (feature flag `attach-warranty-imei-combo`).
 * Quy ước (plan-combo-imei-phase1 §2 D1, §3):
 * - Con IMEI cần N IMEI, N = định mức con / 1 combo × SL dòng combo.
 * - Payload `comboItems`: 1 phần tử / IMEI `{ productId, imeiCode, selectImeiReason }`,
 *   con thường chỉ gửi `{ productId, externalImeiNo }` khi có IMEI BH đính kèm.
 * - GET chi tiết HĐ trả `imeiCodes: []` + `imeiReasons: []` (song song 1-1) cho từng con.
 */

export const toPositiveInteger = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const number = Number(String(value).split(',').join(''));
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : 0;
};

export const isImeiComboChild = (child) =>
  !!child && Number(child.productType) === PRODUCT_TYPE.PRODUCT_IMEI;

export const hasImeiComboChild = (comboItems) =>
  Array.isArray(comboItems) && comboItems.some(isImeiComboChild);

const trimCode = (value) =>
  value === null || value === undefined ? '' : String(value).trim();

/** IMEI của con combo theo đúng index slot (chưa lọc rỗng). */
const getComboChildRawImeiCodes = (child) => {
  if (!child) return [];
  if (Array.isArray(child.imeiCodes)) return child.imeiCodes;
  if (child.imeiCode) return String(child.imeiCode).split(',');
  return [];
};

/**
 * IMEI đang có của con combo kèm lý do bán không theo FIFO:
 * `[{ imeiCode, selectImeiReason }]` (đã trim, bỏ slot rỗng).
 * Ghép lý do theo index slot TRƯỚC khi lọc rỗng để không lệch cặp.
 */
export const getComboChildImeiEntries = (child) => {
  const reasons =
    child && Array.isArray(child.imeiReasons) ? child.imeiReasons : [];
  return getComboChildRawImeiCodes(child)
    .map((code, index) => ({
      imeiCode: trimCode(code),
      selectImeiReason: trimCode(reasons[index]),
    }))
    .filter((entry) => entry.imeiCode);
};

/** Danh sách IMEI đang có của con combo (đã trim, bỏ rỗng). */
export const getComboChildImeiCodes = (child) =>
  getComboChildImeiEntries(child).map((entry) => entry.imeiCode);

export const formatComboChildImeiCodes = (child) =>
  getComboChildImeiCodes(child).join(', ');

/** Dòng combo đã lưu (sửa HĐ) có con IMEI → khóa SL / IMEI (D4). */
export const isComboImeiLocked = (billItem) =>
  !!billItem &&
  !!billItem.isPersisted &&
  hasImeiComboChild(billItem.comboItems);

/** Số IMEI bắt buộc của con IMEI trong dòng combo. */
export const getRequiredImeiCount = (billItem, child) => {
  if (!billItem || !isImeiComboChild(child)) return 0;
  const unitQuantity =
    child.unitQuantity !== undefined && child.unitQuantity !== null
      ? child.unitQuantity
      : child.quantity;
  return toPositiveInteger(unitQuantity) * toPositiveInteger(billItem.quantity);
};

/** IMEI + lý do sai FIFO của con sẽ gửi lên API (dòng mới: chỉ lấy N ô đầu). */
export const getComboChildImeiEntriesForPayload = (billItem, child) => {
  if (!isImeiComboChild(child)) return [];
  if (isComboImeiLocked(billItem)) return getComboChildImeiEntries(child);
  const requiredCount = getRequiredImeiCount(billItem, child);
  const slots = Array.isArray(child.imeiCodes)
    ? child.imeiCodes.slice(0, requiredCount)
    : [];
  const reasonSlots = Array.isArray(child.imeiReasons)
    ? child.imeiReasons.slice(0, requiredCount)
    : [];
  return getComboChildImeiEntries({
    imeiCodes: slots,
    imeiReasons: reasonSlots,
  });
};

/** IMEI của con sẽ gửi lên API (dòng mới: chỉ lấy N ô đầu). */
export const getComboChildImeiCodesForPayload = (billItem, child) =>
  getComboChildImeiEntriesForPayload(billItem, child).map(
    (entry) => entry.imeiCode,
  );

export const buildComboItemsPayload = (billItem) => {
  if (!billItem || !Array.isArray(billItem.comboItems)) return [];
  return billItem.comboItems.reduce((result, child) => {
    if (isImeiComboChild(child)) {
      // Quà / bán kèm bỏ tick không xuất kho => không gửi IMEI con
      if (billItem.checked === false) return result;
      getComboChildImeiEntriesForPayload(billItem, child).forEach((entry) => {
        result.push({
          productId: child.productId,
          imeiCode: entry.imeiCode,
          selectImeiReason: entry.selectImeiReason || null,
        });
      });
      return result;
    }
    // Con thường (phụ kiện PRODUCT): gửi lô (batch-code) để BE trừ đúng lô — mirror
    // buildBillComboItemsPayload. Kèm externalImeiNo (IMEI BH) nếu có.
    const isProduct = Number(child.productType) === PRODUCT_TYPE.PRODUCT;
    const externalImeiNo = child.externalImeiNo
      ? String(child.externalImeiNo).trim()
      : '';
    const productBatchStockId = isProduct ? child.productBatchStockId || 0 : 0;
    if (!externalImeiNo && !(productBatchStockId > 0)) return result;
    const entry = { productId: child.productId };
    if (productBatchStockId > 0) entry.productBatchStockId = productBatchStockId;
    if (externalImeiNo) entry.externalImeiNo = externalImeiNo;
    result.push(entry);
    return result;
  }, []);
};

/**
 * Payload `comboItems` cho HÓA ĐƠN khi có cả batch-code (lô) lẫn IMEI con combo.
 * Gộp 1 lần duyệt để mỗi con thường chỉ xuất 1 entry (tránh trùng productId):
 * - Con IMEI: 1 phần tử / IMEI `{ productId, imeiCode }` (chỉ khi `enableImei`).
 * - Con thường (phụ kiện, PRODUCT_TYPE.PRODUCT): `{ productId, productBatchStockId, externalImeiNo? }`
 *   để BE merge theo productId trừ đúng lô; kèm IMEI BH đính kèm nếu có.
 */
export const buildBillComboItemsPayload = (
  billItem,
  { enableImei = false } = {},
) => {
  if (!billItem || !Array.isArray(billItem.comboItems)) return [];
  return billItem.comboItems.reduce((result, child) => {
    if (isImeiComboChild(child)) {
      // IMEI con combo gate theo flag attach-warranty-imei-combo.
      if (!enableImei) return result;
      // Quà / bán kèm bỏ tick không xuất kho => không gửi IMEI con
      if (billItem.checked === false) return result;
      getComboChildImeiCodesForPayload(billItem, child).forEach((imeiCode) => {
        result.push({ productId: child.productId, imeiCode });
      });
      return result;
    }
    // Con thường: gửi lô (batch-code) cho SP thường (PRODUCT) — BE merge theo productId
    // trừ đúng lô; kèm externalImeiNo (IMEI BH) khi bật flag imei combo và có nhập.
    const isProduct = Number(child.productType) === PRODUCT_TYPE.PRODUCT;
    const externalImeiNo =
      enableImei && child.externalImeiNo
        ? String(child.externalImeiNo).trim()
        : '';
    if (!isProduct && !externalImeiNo) return result;
    const entry = { productId: child.productId };
    if (isProduct) entry.productBatchStockId = child.productBatchStockId || null;
    if (externalImeiNo) entry.externalImeiNo = externalImeiNo;
    result.push(entry);
    return result;
  }, []);
};

/**
 * Chuẩn hóa con combo khi đưa vào listBillItem.
 * - `unitQuantity`: định mức con / 1 combo. Nếu con IMEI đã có IMEI thì suy ra từ
 *   số IMEI / SL dòng (không phụ thuộc `quantity` API trả là định mức hay tổng).
 * - `imeiCodes`: giữ IMEI đã lưu (sửa HĐ) hoặc reset rỗng (tạo mới / sao chép HĐ).
 * - `deriveUnitFromImei = false`: dùng khi IMEI có thể chưa đủ (đơn hàng, HĐ từ đơn) —
 *   API trả `quantity` = định mức / 1 combo (plan §10.5.2).
 */
export const normalizeComboItems = (
  comboItems,
  lineQuantity = 1,
  { keepImeiCodes = false, deriveUnitFromImei = true } = {},
) => {
  if (!Array.isArray(comboItems)) return comboItems;
  const lineQty = toPositiveInteger(lineQuantity);
  return comboItems.map((child) => {
    const entries = getComboChildImeiEntries(child);
    const codes = entries.map((entry) => entry.imeiCode);
    let unitQuantity = toPositiveInteger(child.quantity) || 1;
    if (
      deriveUnitFromImei &&
      isImeiComboChild(child) &&
      codes.length &&
      lineQty &&
      codes.length % lineQty === 0
    ) {
      unitQuantity = codes.length / lineQty;
    }
    return {
      ...child,
      unitQuantity,
      imeiCodes: keepImeiCodes ? codes : [],
      imeiReasons: keepImeiCodes
        ? entries.map((entry) => entry.selectImeiReason)
        : [],
    };
  });
};

const getComboName = (billItem) =>
  billItem.name || billItem.productName || billItem.code || '';

/**
 * Validate IMEI con combo trước khi submit.
 * - `requireAll = false` (đơn hàng ở trạng thái không bắt buộc IMEI): chỉ kiểm trùng
 *   các IMEI đã nhập, không kiểm đủ N / flag.
 * @returns {string} message lỗi, '' nếu hợp lệ.
 */
export const validateComboImei = (
  listBillItem = [],
  { enabled = false, requireAll = true, documentName = 'hóa đơn' } = {},
) => {
  const items = (listBillItem || []).filter(
    (item) => item && item.checked !== false,
  );
  const comboImeiCodes = [];
  for (const billItem of items) {
    if (billItem.flexibleComboId || !hasImeiComboChild(billItem.comboItems)) {
      continue;
    }
    if (isComboImeiLocked(billItem)) {
      billItem.comboItems.forEach((child) => {
        getComboChildImeiCodes(child).forEach((code) =>
          comboImeiCodes.push(code),
        );
      });
      continue;
    }
    if (!requireAll) {
      billItem.comboItems.forEach((child) => {
        getComboChildImeiCodesForPayload(billItem, child).forEach((code) =>
          comboImeiCodes.push(code),
        );
      });
      continue;
    }
    if (!enabled) {
      return `Sản phẩm combo ${getComboName(
        billItem,
      )} có sản phẩm IMEI chưa được hỗ trợ`;
    }
    for (const child of billItem.comboItems) {
      if (!isImeiComboChild(child)) continue;
      const requiredCount = getRequiredImeiCount(billItem, child);
      const codes = getComboChildImeiCodesForPayload(billItem, child);
      if (codes.length < requiredCount) {
        return `Vui lòng nhập đủ ${requiredCount} IMEI cho sản phẩm ${
          child.productName || ''
        } trong combo ${getComboName(billItem)}`;
      }
      codes.forEach((code) => comboImeiCodes.push(code));
    }
  }
  if (!comboImeiCodes.length) return '';

  const usedCodes = new Set(
    items
      .filter((item) => !item.flexibleComboId && item.imeiCode)
      .map((item) => String(item.imeiCode).trim())
      .filter(Boolean),
  );
  for (const code of comboImeiCodes) {
    if (usedCodes.has(code)) {
      return `IMEI ${code} bị trùng trong ${documentName}`;
    }
    usedCodes.add(code);
  }
  return '';
};

/**
 * Tất cả IMEI đang được dùng trong HĐ (dòng thường + con combo), đã trim & bỏ trùng.
 * Dùng để loại khỏi danh sách IMEI của modal chọn máy (giữ đúng thứ tự FIFO còn lại).
 */
export const collectImeiCodesInBill = (listBillItem = []) => {
  const codes = new Set();
  (listBillItem || []).forEach((item) => {
    if (!item) return;
    const ownCode = item.imeiCode ? String(item.imeiCode).trim() : '';
    if (ownCode) codes.add(ownCode);
    if (item.flexibleComboId || !Array.isArray(item.comboItems)) return;
    item.comboItems.forEach((child) => {
      if (!isImeiComboChild(child)) return;
      // Dùng chung định nghĩa "IMEI đang dùng" với isImeiCodeInBill (chỉ N slot đầu)
      getComboChildImeiCodesForPayload(item, child).forEach((code) =>
        codes.add(code),
      );
    });
  });
  return Array.from(codes);
};

/** IMEI đã tồn tại trong HĐ (dòng thường + con combo). */
export const isImeiCodeInBill = (listBillItem = [], imeiCode) => {
  const code = imeiCode ? String(imeiCode).trim() : '';
  if (!code) return false;
  return (listBillItem || []).some(
    (item) =>
      (item.imeiCode && String(item.imeiCode).trim() === code) ||
      (!item.flexibleComboId &&
        Array.isArray(item.comboItems) &&
        item.comboItems.some(
          (child) =>
            isImeiComboChild(child) &&
            getComboChildImeiCodesForPayload(item, child).includes(code),
        )),
  );
};

/**
 * Payload `comboItems` cho đơn hàng: chỉ con IMEI (plan §10.5.1).
 * Gửi kèm `selectImeiReason` — `OrderItemCombos.selectImeiReason` lưu theo từng máy,
 * và `syncFromBill` mang ngược lý do từ HĐ về đơn khi xuất hàng.
 */
export const buildOrderComboItemsPayload = (orderItem) =>
  buildComboItemsPayload(orderItem).filter(
    (item) => item.imeiCode || item.productBatchStockId > 0,
  );

/**
 * Khóa so sánh tập IMEI con theo từng combo: { [comboProductId]: 'childId:imei|...' }.
 * Dùng chặn đổi IMEI con khi đơn Đang giao → Đang giao (plan §10.9 Q2).
 */
export const buildComboImeiKeyMap = (items = []) => {
  const groups = {};
  (items || []).forEach((item) => {
    if (!item || item.deleted || !hasImeiComboChild(item.comboItems)) return;
    const key = item.productId;
    groups[key] = groups[key] || [];
    item.comboItems.forEach((child) => {
      getComboChildImeiCodesForPayload(item, child).forEach((code) =>
        groups[key].push(`${child.productId}:${code}`),
      );
    });
  });
  return Object.keys(groups).reduce((result, key) => {
    result[key] = groups[key].sort().join('|');
    return result;
  }, {});
};

/**
 * productId combo đầu tiên có tập IMEI con khác nhau giữa 2 khóa, null nếu giống.
 * `ignoreEmptyBefore`: combo lúc load chưa có IMEI con thì cho nhập (không coi là đổi).
 */
export const findChangedComboImeiProductId = (
  beforeMap = {},
  afterMap = {},
  { ignoreEmptyBefore = false } = {},
) => {
  const keys = new Set([...Object.keys(beforeMap), ...Object.keys(afterMap)]);
  for (const key of keys) {
    const before = beforeMap[key] || '';
    if (ignoreEmptyBefore && !before) continue;
    if (before !== (afterMap[key] || '')) return key;
  }
  return null;
};

/* ---------- GĐ2 — Trả hàng combo có máy IMEI (plan-combo-imei-phase2 §7) ---------- */

/** HĐ bán có dữ liệu IMEI máy trong combo (snapshot). HĐ cũ trước GĐ1 => false. */
export const hasRefundComboSnapshot = (billItem) =>
  !!billItem &&
  hasImeiComboChild(billItem.comboItems) &&
  billItem.comboItems.some(
    (child) => isImeiComboChild(child) && getComboChildImeiCodes(child).length,
  );

/** IMEI còn có thể trả của con = đã bán − đã trả ở HĐ trả trước. */
export const getRefundAvailableImeiCodes = (child) => {
  const refunded = new Set(
    (Array.isArray(child && child.refundedImeiCodes)
      ? child.refundedImeiCodes
      : []
    )
      .map((code) => String(code).trim())
      .filter(Boolean),
  );
  return getComboChildImeiCodes(child).filter((code) => !refunded.has(code));
};

/** SL combo còn có thể trả suy từ máy còn lại (min qua các con IMEI). */
export const getRefundRemainingQuantity = (billItem) => {
  if (!hasRefundComboSnapshot(billItem)) return 0;
  const counts = billItem.comboItems.filter(isImeiComboChild).map((child) => {
    const unit = toPositiveInteger(child.unitQuantity) || 1;
    return Math.floor(getRefundAvailableImeiCodes(child).length / unit);
  });
  return counts.length ? Math.min(...counts) : 0;
};

/** Trả đủ máy còn lại => tick hết & khóa checkbox. */
export const isRefundComboSelectionLocked = (billItem, child) =>
  getRequiredImeiCount(billItem, child) >=
  getRefundAvailableImeiCodes(child).length;

/**
 * Đồng bộ máy đã tick với SL trả: đủ SL => tick hết; thừa => cắt bớt; bỏ máy đã trả.
 * Mutate `child.selectedImeiCodes` (dùng Vue.set-safe: gán mảng mới).
 */
export const syncRefundComboSelection = (billItem) => {
  if (!hasRefundComboSnapshot(billItem)) return;
  billItem.comboItems.forEach((child) => {
    if (!isImeiComboChild(child)) return;
    const available = getRefundAvailableImeiCodes(child);
    const required = getRequiredImeiCount(billItem, child);
    const selected = (child.selectedImeiCodes || []).filter((code) =>
      available.includes(code),
    );
    if (required >= available.length) {
      child.selectedImeiCodes = available.slice();
    } else {
      child.selectedImeiCodes = selected.slice(0, required);
    }
  });
};

/** Lỗi inline của 1 con IMEI ('' nếu hợp lệ / không phải con IMEI / HĐ cũ). */
export const getRefundComboChildError = (billItem, child) => {
  if (!isImeiComboChild(child) || !getComboChildImeiCodes(child).length) {
    return '';
  }
  const required = getRequiredImeiCount(billItem, child);
  const selected = (child.selectedImeiCodes || []).length;
  if (selected === required) return '';
  // GĐ2 M-10: SL trả vượt số máy còn lại (chứ không phải thiếu tick) => báo rõ số máy còn trả
  // được thay vì message "chọn đủ N" gây khó hiểu.
  const available = getRefundAvailableImeiCodes(child);
  if (required > available.length) {
    return `Combo chỉ còn ${available.length} máy ${
      child.productName || ''
    } có thể trả, vui lòng giảm số lượng trả`;
  }
  return `Vui lòng chọn đủ ${required} IMEI cho sản phẩm ${
    child.productName || ''
  } (đã chọn ${selected})`;
};

/** Validate toàn HĐ trả trước submit: '' nếu hợp lệ. */
export const validateRefundComboImei = (listBillItem = []) => {
  for (const billItem of listBillItem || []) {
    if (!billItem || billItem.checked === false) continue;
    if (!hasRefundComboSnapshot(billItem)) continue;
    for (const child of billItem.comboItems) {
      const error = getRefundComboChildError(billItem, child);
      if (error) {
        return `${error} trong combo ${getComboName(billItem)}`;
      }
    }
  }
  return '';
};

/** Payload `comboItems` cho POST bills/refund-bill: chỉ máy đã tick (con thường không gửi). */
export const buildRefundComboItemsPayload = (billItem) => {
  if (!billItem || !Array.isArray(billItem.comboItems)) return [];
  // GĐ2 M-9: đồng bộ buildComboItemsPayload — quà / bán kèm bỏ tick không xuất/nhập kho
  // => không gửi IMEI con.
  if (billItem.checked === false) return [];
  return billItem.comboItems.reduce((result, child) => {
    if (!isImeiComboChild(child)) return result;
    (child.selectedImeiCodes || []).forEach((imeiCode) =>
      result.push({ productId: child.productId, imeiCode }),
    );
    return result;
  }, []);
};
